import { isPlatformApiError } from '@traceio-org/platform-client';
import type { ApplicationRegistry } from '@traceio-org/platform-contracts';
import { hostStateFromResolution, isTraceLocalHost, type HostState } from '../security/host';
import type { HubDependencies } from './deps';

/** Loaded once per page load by the root route, before any page renders. */
export interface HubBootstrap {
  host: HostState;
  /** Null when the registry could not be loaded; every redirect is then refused. */
  registry: ApplicationRegistry | null;
}

/**
 * Resolves the address the hub is served on. localhost and 127.0.0.1 are Trace's own sign-in; any other
 * host must be a verified, active domain in platform-api. Any failure (unknown host, unverified host,
 * refused origin, unreachable API) ends in the unknown state, which never shows a sign-in form.
 */
export async function resolveHost(deps: HubDependencies): Promise<HostState> {
  const { hostname } = deps.location;
  if (isTraceLocalHost(hostname)) return { kind: 'trace', forcedProduct: null };
  try {
    return hostStateFromResolution(await deps.client.resolveDomain(hostname));
  } catch (error) {
    const definitive = isPlatformApiError(error) && error.kind === 'http' && error.status >= 400 && error.status < 500;
    if (!definitive) console.warn('account-hub: could not resolve this host', error);
    return { kind: 'unknown', reason: definitive ? 'not_set_up' : 'unreachable' };
  }
}

async function loadRegistry(deps: HubDependencies): Promise<ApplicationRegistry | null> {
  try {
    return await deps.client.applications();
  } catch (error) {
    console.warn('account-hub: application registry unavailable; redirects will be refused', error);
    return null;
  }
}

export async function loadBootstrap(deps: HubDependencies): Promise<HubBootstrap> {
  const [host, registry] = await Promise.all([resolveHost(deps), loadRegistry(deps)]);
  return { host, registry };
}
